import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import config from "../config";
import Viewer360Modal from './Viewer360Modal';
import useTranslation from './useTranslation';

const Icon = ({ name, className }) => (
  <span className={`material-symbols-outlined ${className || ''}`}>
    {name}
  </span>
);

const getYouTubeEmbedUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 
    ? `https://www.youtube.com/embed/${match[2]}`
    : null;
};

const getVimeoEmbedUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const regExp = /(?:vimeo\.com\/)([0-9]+)/;
  const match = url.match(regExp);
  return match && match[1] ? `https://player.vimeo.com/video/${match[1]}` : null;
};

const renderVideoEmbed = (videoField) => {
    if (!videoField) return null;
    
    return videoField.map((video, index) => {
      if (!video?.value) return null;
      
      const videoUrl = video.value;
      const embedUrl = getYouTubeEmbedUrl(videoUrl) || getVimeoEmbedUrl(videoUrl);
      
      if (!embedUrl) return null;
  
      return (
        <div key={index} className="media-container mb-4">
          <div className="ratio ratio-16x9">
            <iframe
              src={embedUrl}
              title={`Video ${index + 1}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="rounded"
            ></iframe>
          </div>
        </div>
      );
    });
  };

const renderAudioPlayer = (audioFiles) => {
    if (!audioFiles || !audioFiles.length) return null;
  
    // Funzione per gestire il play di un audio
    const handlePlay = (currentIndex) => {
      audioFiles.forEach((_, index) => {
        if (index !== currentIndex) {
          // Trova e metti in pausa tutti gli altri audio player
          const audioElement = document.getElementById(`audio-player-${index}`);
          if (audioElement) {
            audioElement.pause();
          }
        }
      });
    };

    return audioFiles.map((audio, index) => {
        const audioUrl = audio.field_media_audio_file?.[0]?.url;
        const audioName = audio.name?.[0]?.value;
    
        if (!audioUrl) return null;
    
        return (
          <div key={index} className="media-container mb-4 mt-5">
            <div className="">
              <audio 
                id={`audio-player-${index}`}
                controls 
                className="w-100"
                onPlay={() => handlePlay(index)}
              >
                <source src={audioUrl} type="audio/mpeg" />
                Il tuo browser non supporta l'elemento audio.
              </audio>
            </div>
            {audioName && (
              <p className="m-2 text-muted">{audioName}</p>
            )}
          </div>
        );
      });
    };

// Funzione per renderizzare l'anteprima dell'immagine 360°
const render360Preview = (image, title, t, onOpen) => {
  if (!image || !image.url) return null;
  
  return (
    <div 
      className="position-relative rounded overflow-hidden mb-4" 
      style={{ cursor: 'pointer' }}
      onClick={onOpen}
    >
      {/* Immagine di sfondo */}
      <img
        src={image.url}
        alt={image.alt || 'Immagine 360'}
        className="w-100 d-block"
        style={{ 
          maxHeight: '400px',
          objectFit: 'cover'
        }}
      />
      
      {/* Overlay scuro */}
      <div 
        className="position-absolute w-100 h-100 top-0 left-0" 
        style={{ 
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
        }}
      />
      
      {/* Contenuto centrale con icona e testo */}
      <div 
        className="position-absolute top-50 start-50 translate-middle text-center text-white"
        style={{ zIndex: 2 }}
      >
        <Icon 
          name="360" 
          className="d-block mx-auto mb-2 text-white" 
          style={{ fontSize: '3rem' }} 
        />
        <p className="mb-0 text-white">{t('Clicca per navigare a 360°')}</p>
      </div>
    </div>
  );
};

const FocusPanel = ({ isOpen, onClose, focus, taxonomyData }) => {
  const { t } = useTranslation();
  const [focusAttachments, setFocusAttachments] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [audioFiles, setAudioFiles] = useState([]);
  const panelRef = useRef(null);
  const [panelScrollPosition, setPanelScrollPosition] = useState(0);
  
  // Stati per gestione immagini 360°
  const [is360Image, setIs360Image] = useState(false);
  const [viewer360Open, setViewer360Open] = useState(false);
  const [current360Image, setCurrent360Image] = useState(null);

  const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);

  // Sposta la dichiarazione di fetchMediaDetails prima del suo utilizzo
  const fetchMediaDetails = useCallback(async (mediaList) => {
    if (!mediaList || mediaList.length === 0) return [];
    
    const mediaResponses = await Promise.all(
      mediaList.map(media =>
        axios.get(config.API_ENDPOINTS.getMedia(media.target_id, selectedLang))
      )
    );
    return mediaResponses.map(res => res.data);
  }, [selectedLang]);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsAnimating(false);
      onClose();
    }, 300);
  };
  
  // Funzione per aprire il visualizzatore 360°
  const open360Viewer = () => {
    if (focus?.field_immagine_360deg && focus.field_immagine_360deg.length > 0) {
      // Salva la posizione di scroll corrente prima di aprire il viewer
      if (panelRef.current) {
        setPanelScrollPosition(panelRef.current.scrollTop);
      }
      setCurrent360Image(focus.field_immagine_360deg[0]);
      setViewer360Open(true);
    }
  };
  
  // Controllo se il focus ha un'immagine 360°
  useEffect(() => {
    if (focus) {
      const has360Image = focus?.field_immagine_360deg && focus.field_immagine_360deg.length > 0;
      setIs360Image(has360Image);
    } else {
      setIs360Image(false);
    }
  }, [focus]);

  // Gestione della posizione di scroll quando si passa tra Focus e Viewer360
  useEffect(() => {
    if (viewer360Open && panelRef.current) {
      // Blocca lo scroll del pannello quando il viewer è aperto
      panelRef.current.style.overflowY = 'hidden';
    } else if (!viewer360Open && isOpen && panelRef.current) {
      // Ripristina lo scroll del pannello quando si chiude il viewer
      panelRef.current.style.overflowY = 'auto';
      
      // Ripristina la posizione di scroll del pannello
      setTimeout(() => {
        if (panelRef.current) {
          panelRef.current.scrollTop = panelScrollPosition;
        }
      }, 50);
    }
  }, [viewer360Open, isOpen, panelScrollPosition]);

  // Gestione degli allegati del focus
  useEffect(() => {
    setFocusAttachments([]);
    setAudioFiles([]);

    const fetchData = async () => {
      try {
        if (focus?.field_allegati?.length > 0) {
          const attachments = await fetchMediaDetails(focus.field_allegati);
          setFocusAttachments(attachments);
        }

        if (focus?.field_audio?.length > 0) {
          const audios = await fetchMediaDetails(focus.field_audio);
          setAudioFiles(audios);
        }
      } catch (error) {
        console.error('Errore nel recupero dei media:', error);
      }
    };

    fetchData();
  }, [focus, fetchMediaDetails]);

  // Gestione dello scroll del body quando il pannello è aperto
  useEffect(() => {
    if (isOpen) {
      // Blocca lo scroll del body quando il focus è aperto
      document.body.style.overflow = 'hidden';
    } else {
      // Ripristina lo scroll del body quando il focus è chiuso
      document.body.style.overflow = '';
    }

    return () => {
      // Cleanup
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Inizializzazione del carousel quando il focus è aperto
  useEffect(() => {
    if (isOpen && focus?.field_image?.length > 0) {
      const carouselElement = document.getElementById('focusPanelCarousel');
      if (carouselElement) {
        new window.bootstrap.Carousel(carouselElement, {
          interval: false,
          touch: true
        });
      }
    }
  }, [isOpen, focus]);

  const panelClasses = [
    'focus-panel',
    isOpen ? 'is-open' : '',
    isAnimating ? 'is-closing' : '',
  ].join(' ');

  // Rendering del pannello, RIMOZIONE DEGLI STILI INLINE che sovrascrivono gli stili CSS
  return (
    <div 
      ref={panelRef}
      className={panelClasses}
    >
      {focus && (
        <>
          <div className="sticky-top bg-white border-bottom">
            <div className="container d-flex justify-content-between align-items-center py-3">
              <button
                onClick={handleClose}
                aria-label="Chiudi"
                className="btn bg-white d-flex align-items-center justify-content-center ratio-1x1 goback"
                style={{ position: 'relative', top: 0, left: 0 }}
              >
                <Icon name="close" />
              </button>
              <div className="d-flex align-items-center">
                {taxonomyData?.field_icona?.[0]?.alt && (
                  <Icon 
                    name={taxonomyData.field_icona[0].alt} 
                    className="me-2" 
                    style={{ width: '24px', height: '24px' }} 
                  />
                )}
              </div>                                                                                                                      
            </div>
          </div>

          {focus?.field_image?.length > 0 && (
            <div id="focusPanelCarousel" className="carousel slide">
              {focus.field_image.length > 1 && (
                <ol className="carousel-indicators">
                  {focus.field_image.map((_, index) => (
                    <li
                      key={index}
                      data-bs-target="#focusPanelCarousel"
                      data-bs-slide-to={index}
                      className={index === 0 ? 'active' : ''}
                    />
                  ))}
                </ol>
              )}
              <div className="carousel-inner">
                {focus.field_image.map((image, index) => (
                  <div
                    className={`carousel-item ${index === 0 ? 'active' : ''}`}
                    key={index}
                  >
                    <img
                      src={image.url}
                      className="d-block w-100"
                      alt={image.alt || 'Immagine'}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="container description text-content pb-0">
            <h1 className="mb-4 mt-4">{focus?.title?.[0]?.value}</h1>
            {focus?.field_descrizione_breve?.[0]?.value && (
              <div className="info-box mb-3 border align-items-center">
                <Icon name="info" />
                <div 
                  className="info-text"
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(focus.field_descrizione_breve[0].value || '') 
                  }} 
                />
              </div>
            )}

            <div
              className="poi-body mb-4"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(focus?.body?.[0]?.value || ''),
              }}
            />

            {/* Media Content */}
            <div className="media-content mb-5">
              {audioFiles.length > 0 && renderAudioPlayer(audioFiles)}
              {focus?.field_video?.length > 0 && renderVideoEmbed(focus.field_video)}
              {is360Image && focus?.field_immagine_360deg?.length > 0 && 
                render360Preview(
                  focus.field_immagine_360deg[0],
                  focus?.title?.[0]?.value,
                  t,
                  open360Viewer
                )
              }
            </div>

            {focusAttachments?.length > 0 && (
              <div className="mb-5">
                <h2>Allegati</h2>
                <ul className="list-unstyled">
                  {focusAttachments.map((media, index) => {
                    const fileUrl = media.field_media_document?.[0]?.url;
                    return fileUrl ? (
                      <li key={index} className="mb-2">
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-flex align-items-center justify-content-start link-primary fw-semibold text-decoration-none"
                        >
                          <Icon name="attach_file" className="link-primary" />
                          {media.name[0].value || 'Documento'}
                        </a>
                      </li>
                    ) : (
                      <li key={index} className="mb-2 text-muted">File non disponibile</li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          
          {/* Componente visualizzatore 360° separato */}
          <Viewer360Modal 
            isOpen={viewer360Open}
            onClose={() => setViewer360Open(false)}
            imageUrl={current360Image?.url}
            imageTitle={focus?.title?.[0]?.value || current360Image?.alt}
          />
        </>
      )}
    </div>
  );
};

export default FocusPanel;